<script setup lang="ts">
import { computed, onActivated, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { i18n } from '@/plugins/i18n'
import { handleApiRequest, isAxiosError } from '@/api/handleApiRequest'
import {
  getZBEdgeUserDetail,
  updateZBEdgeUserDetail,
  type GetZBEdgeUserDetailResponse,
  type UpdateZBEdgeUserDetailRequest
} from '@/api/endpoints/zbedgeUserWeb'
import { getAntiForgeryToken } from '@/api/endpoints/userWeb'
import { formatPhoneNumber } from '@/utils/phoneFormatter'
import type { PhoneTelNumberInputModel, VueTelInputData } from '@/types/phoneTelNumberInputTypes'
import { formatDisplayedDateTime } from '@/utils/dateFormatter'

const props = defineProps<{ userId: string }>()

const loaded = ref(false)
const pageErrorMessage = ref('')
const user: Ref<GetZBEdgeUserDetailResponse | undefined> = ref()
const editMode = ref(false)
const pendingProfile = ref<UpdateZBEdgeUserDetailRequest>({} as UpdateZBEdgeUserDetailRequest)
const phoneTelNumberInputModel = ref<PhoneTelNumberInputModel>({
  value: '',
  phoneObject: {} as VueTelInputData
})
const form = ref()
const $q = useQuasar()
const router = useRouter()

let initialFormState = ''

onActivated(async () => {
  editMode.value = false
  await loadUser()
})

const loadUser = async () => {
  loaded.value = false
  $q.loading.show()

  const response = await handleApiRequest<GetZBEdgeUserDetailResponse>(() => getZBEdgeUserDetail(props.userId))
  const data = response?.data as any

  if (data?.userId) {
    user.value = data as GetZBEdgeUserDetailResponse
  } else {
    pageErrorMessage.value = i18n.global.t('error_unexpected_occured').toString()
  }

  loaded.value = true
  $q.loading.hide()
}

watch(phoneTelNumberInputModel, (newVal) => {
  if (editMode.value && newVal.phoneObject?.nationalNumber !== undefined) {
    pendingProfile.value.phone = newVal.phoneObject.nationalNumber ?? ''
    pendingProfile.value.phoneCountryCode = newVal.phoneObject.countryCallingCode ?? ''
  }
}, { deep: true })



const dashesIfMissing = (value: string | null | undefined) => value || '--'

const toUsersList = () => router.push({ name: 'ZBEdgeUsers' })

const toggleEdit = () => {
  form.value?.resetValidation()
  editMode.value = !editMode.value

  if (editMode.value && user.value) {
    const data: UpdateZBEdgeUserDetailRequest = {
      userId: user.value.userId,
      firstName: user.value.firstName ?? '',
      lastName: user.value.lastName ?? '',
      prefix: user.value.prefix,
      suffix: user.value.suffix,
      npi: user.value.npi,
      phone: user.value.phone ?? '',
      phoneCountryCode: user.value.phoneCountryCode ?? '',
      email: user.value.email ?? ''
    }
    phoneTelNumberInputModel.value.value = user.value.phone
      ? '+' + (user.value.phoneCountryCode ?? '') + user.value.phone
      : ''
    initialFormState = JSON.stringify(data)
    pendingProfile.value = data
  }
}

const isDirty = computed(() => initialFormState !== JSON.stringify(pendingProfile.value))

const saveUser = async () => {
  const isValid = await form.value.validate()
  if (!isValid) return

  $q.loading.show()

  await getAntiForgeryToken()
  const response = await handleApiRequest(() => updateZBEdgeUserDetail(pendingProfile.value))

  if (!isAxiosError(response) && !response?.data?.errorMessage) {
    $q.notify({
      message: i18n.global.t('edit_profile_changes_saved').toString(),
      type: 'message'
    })
    editMode.value = false
    await loadUser()
  } else {
    const message = (response as any)?.data?.errorMessage || i18n.global.t('error_unexpected_occured').toString()
    $q.notify({
      message,
      type: 'warning'
    })
  }

  $q.loading.hide()
}
</script>

<template>
  <q-page padding id="zbedgeUserDetailPage">
    <div id="pageError" v-if="pageErrorMessage">
      <div class="errorMessage">{{ pageErrorMessage }}</div>
      <button class="button cancel" type="button" @click="toUsersList">{{ $t('gen_return_to_list') }}</button>
    </div>

    <div v-if="loaded && !pageErrorMessage && user">
      <div id="zbedgeUserDetailHeader">
        <h1>
          <q-icon name="far fa-solid fa-chevron-left" class="cursor-pointer" @click="toUsersList" />
          {{ user.firstName }} {{ user.lastName }}
        </h1>
      </div>

      <div id="zbedgeUserDetailsSection">
        <div id="zbedgeUserDetails" :class="{ editMode: editMode }">
          <div class="panelActionButtonWrap">
            <q-icon name="far fa-solid fa-pencil" class="editIcon cursor-pointer" @click="toggleEdit" />
            <q-icon name="far fa-solid fa-xmark" class="cancelIcon cursor-pointer" @click="toggleEdit" />
          </div>
          <h5>{{ $t('zbedge_user_detail_title') }}</h5>

          <q-form @submit.prevent ref="form">
            <div class="row">
              <div class="column">
                <dl>
                  <dt>{{ $t('gen_first_name') }}</dt>
                  <dd>
                    <span v-if="!editMode">{{ dashesIfMissing(user.firstName) }}</span>
                    <zbm-input-with-validation v-else v-model="pendingProfile.firstName"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.required(), $zb.validators.maxLen(128)]" />
                  </dd>

                  <dt>{{ $t('gen_last_name') }}</dt>
                  <dd>
                    <span v-if="!editMode">{{ dashesIfMissing(user.lastName) }}</span>
                    <zbm-input-with-validation v-else v-model="pendingProfile.lastName"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.required(), $zb.validators.maxLen(128)]" />
                  </dd>

                  <dt>{{ $t('gen_prefix') }}</dt>
                  <dd>
                    <span v-if="!editMode">{{ dashesIfMissing(user.prefix) }}</span>
                    <zbm-input-with-validation v-else v-model="pendingProfile.prefix"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.maxLen(128)]" />
                  </dd>

                  <dt>{{ $t('gen_suffix') }}</dt>
                  <dd>
                    <span v-if="!editMode">{{ dashesIfMissing(user.suffix) }}</span>
                    <zbm-input-with-validation v-else v-model="pendingProfile.suffix"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.maxLen(128)]" />
                  </dd>

                  <dt>{{ $t('gen_npi') }}</dt>
                  <dd>
                    <span v-if="!editMode">{{ dashesIfMissing(user.npi) }}</span>
                    <zbm-input-with-validation v-else v-model="pendingProfile.npi"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.maxLen(100)]" />
                  </dd>

                  <dt>{{ $t('gen_federated') }}</dt>
                  <dd>{{ user.isFederated ? $t('gen_yes') : $t('gen_no') }}</dd>
                </dl>
              </div>

              <div class="column">
                <dl>
                  <dt>{{ $t('gen_email') }}</dt>
                  <dd>
                    <span v-if="!editMode || user.isFederated">{{ dashesIfMissing(user.email) }}</span>
                    <zbm-input-with-validation v-else v-model="pendingProfile.email"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.required(), $zb.validators.email(), $zb.validators.maxLen(128)]" />
                  </dd>

                  <dt>{{ $t('gen_phone') }}</dt>
                  <dd>
                    <span v-if="!editMode">{{ formatPhoneNumber(user.phoneCountryCode, user.phone) }}</span>
                    <zbm-telephone-with-validation v-else v-model="phoneTelNumberInputModel"
                      :countryCode="pendingProfile.phoneCountryCode"
                      :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                      :rules="[$zb.validators.required()]" />
                  </dd>

                  <dt>{{ $t('gen_last_login') }}</dt>
                  <dd>{{ formatDisplayedDateTime(user.lastLoginTime) }}</dd>

                  <dt>{{ $t('gen_status') }}</dt>
                  <dd>{{ user.isDeactivated ? $t('gen_deactivated') : $t('account_status_active') }}</dd>

                  <dt>{{ $t('gen_applications') }}</dt>
                  <dd>
                    <ul v-if="user.applications && user.applications.length > 0">
                      <li v-for="app in user.applications" :key="app.appId">{{ app.appName }}</li>
                    </ul>
                    <span v-else>--</span>
                  </dd>
                </dl>
              </div>
            </div>

            <div id="editControls" v-if="editMode">
              <div class="column items-end">
                <div class="col q-gutter-sm">
                  <zbm-btn
                    @click="toggleEdit"
                    :color="$zb.enums.ButtonStdColorEnum.darkTealText"
                    :height="$zb.enums.ButtonHeightEnum.md"
                    :label="$t('gen_cancel')"
                  />
                  <zbm-btn
                    @click="saveUser"
                    :color="$zb.enums.ButtonStdColorEnum.darkTeal"
                    :height="$zb.enums.ButtonHeightEnum.md"
                    :label="$t('gen_save_changes')"
                    :disabled="!isDirty"
                    :type="$zb.enums.ButtonTypeEnum.submit"
                  />
                </div>
              </div>
            </div>
          </q-form>
        </div>
      </div>
    </div>
  </q-page>
</template>

<style lang="scss">
@import "@/css/styles-quasar";

#zbedgeUserDetailPage {
  #pageError {
    text-align: center;
    font-weight: bold;
    padding: $space-double;

    .errorMessage {
      margin-bottom: $space-double;
      font-size: 18px;
    }
  }

  dl {
    display: grid;
    grid-gap: 4px 16px;
    grid-template-columns: auto minmax(0, 1fr);
    align-content: flex-start;
    padding: 0;
    max-width: 100%;

    dt {
      color: $body-dim-txt-clr;
    }

    dd {
      margin: 0;
      grid-column-start: 2;
      overflow-wrap: anywhere;

      ul {
        margin: 0;
        padding-left: 18px;
      }
    }
  }

  .row {
    gap: $space-base;
    justify-content: space-between;
    display: flex;
    flex-direction: row;
    max-width: 100%;

    .column {
      flex: 1;
      max-width: 48%;
    }
  }

  #zbedgeUserDetailHeader {
    margin-bottom: $space-double;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: $space-base;

    .fa-chevron-left {
      font-weight: bold;
      margin-right: $space-base;
    }
  }

  #zbedgeUserDetailsSection {
    #zbedgeUserDetails {
      @extend .panel;
      position: relative;

      .panelActionButtonWrap {
        float: right;
        text-align: right;
        width: 20px;
        height: 20px;

        .editIcon,
        .cancelIcon {
          width: 20px;
          height: 20px;
        }

        .cancelIcon {
          display: none;
        }
      }

      &.editMode {
        .panelActionButtonWrap {
          .editIcon {
            display: none;
          }

          .cancelIcon {
            display: block;
          }
        }
      }

      #editControls {
        border-top: 1px solid $border-clr;
        margin-top: $space-base;
        padding-top: $space-base;
      }
    }
  }

  @media screen and (max-width: $breakpoint-lg) {
    .row {
      flex-direction: column;

      .column {
        max-width: 100%;
      }
    }
  }
}
</style>
